// Getting a model into the browser and ready to run.
//
// Two tools need this now, so it sits here rather than inside either of them:
// the runtime is loaded once per page whatever asks for it, and the download of
// a model is the slowest thing on the page and is worth caching exactly once.
//
// The files come from this origin, put there by scripts/build-tools-assets.js.
// Nothing here reaches anywhere else, which is what lets the pages promise that
// a picture never leaves the machine.

const RUNTIME_URL = '/plugins/onnxruntime/ort.wasm.min.js';
const RUNTIME_PATH = '/plugins/onnxruntime/';
const MODEL_CACHE = 'imageserver-tools-models-v1';

// What the runtime itself costs on first use, on top of the model: the loader,
// its glue module and the 12.7 MB WebAssembly binary. Only used for the sizes
// the pages quote, so it has to follow scripts/build-tools-assets.js when the
// onnxruntime-web version changes, and nothing breaks if it lags a little.
export const RUNTIME_BYTES = 46627 + 26583 + 12666427;

/**
 * Something the visitor can act on, out of whatever loading threw.
 *
 * The raw messages ("Failed to fetch", a script error with no detail) say
 * nothing about the one case that matters most here: being offline before the
 * tool has ever been downloaded, which looks exactly like a broken page.
 */
export function explainLoadError(err) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return 'You are offline, and this tool has not been downloaded to this browser yet. '
            + 'Connect once and try again; after that it works without a connection.';
    }
    const message = err && err.message ? err.message : '';
    if (/runtime|model|fetch|network|load/i.test(message)) {
        return 'The tool could not be downloaded. Check your connection and try again.';
    }
    return message || 'Something went wrong. Try again.';
}

let runtimePromise = null;
const sessions = new Map();

/** Load ONNX Runtime once, and point it at the wasm sitting next to it. */
export function loadRuntime() {
    if (runtimePromise) return runtimePromise;

    runtimePromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = RUNTIME_URL;
        script.onload = () => {
            if (!window.ort) {
                reject(new Error('The image runtime loaded but did not start'));
                return;
            }
            // Without this the runtime looks for its wasm next to the page.
            window.ort.env.wasm.wasmPaths = RUNTIME_PATH;
            // The page is not cross-origin isolated, so SharedArrayBuffer is
            // unavailable and threads cannot work. Saying so up front skips a
            // failed worker start and a console error on every run.
            window.ort.env.wasm.numThreads = 1;
            resolve(window.ort);
        };
        script.onerror = () => {
            // Gone again, so the next attempt adds a fresh tag instead of
            // waiting on one that has already failed.
            script.remove();
            reject(new Error('Could not load the image runtime'));
        };
        document.head.appendChild(script);
    });

    // A failed load is not kept: without this, every later attempt on the page
    // would get the same rejection and "Try again" could never work.
    runtimePromise.catch(() => { runtimePromise = null; });
    return runtimePromise;
}

/**
 * The model bytes, from the Cache Storage if they are already there.
 *
 * Read with a stream rather than as one blob so a large model can report how
 * far along it is. Cache Storage needs a secure context, so a plain http origin
 * quietly falls back to downloading each time.
 */
async function fetchModel(model, onProgress) {
    let cache = null;
    try {
        cache = await caches.open(MODEL_CACHE);
        const hit = await cache.match(model.file);
        if (hit) return await hit.arrayBuffer();
    } catch (err) {
        // No Cache Storage here. Not worth telling anyone about; it costs a
        // download on the next visit and nothing else.
    }

    const response = await fetch(model.file);
    if (!response.ok) throw new Error(`Could not load the ${model.label.toLowerCase()} model`);

    const total = Number(response.headers.get('content-length')) || model.bytes;
    const reader = response.body?.getReader();
    if (!reader) return await response.arrayBuffer();

    const chunks = [];
    let received = 0;
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (onProgress) onProgress(Math.min(1, received / total));
    }

    const bytes = new Uint8Array(received);
    let offset = 0;
    for (const chunk of chunks) {
        bytes.set(chunk, offset);
        offset += chunk.length;
    }

    // Cached from the bytes rather than from a clone of the response. Handing
    // the cache a clone makes it consume the whole body before this reader sees
    // a chunk, which leaves the progress bar at nothing for the entire download
    // and then finishes it in one jump.
    if (cache) {
        try {
            await cache.put(model.file, new Response(bytes));
        } catch (err) {
            // A full quota, or a browser that will not store something this
            // large. It costs a download on the next visit and nothing else.
        }
    }

    return bytes.buffer;
}

/**
 * An inference session for a model, kept so a second image reuses it.
 *
 * @param {{id: string, file: string, label: string, bytes: number}} model
 * @param {Function} [onProgress] Download progress, 0 to 1.
 */
export function sessionFor(model, onProgress) {
    if (sessions.has(model.id)) return sessions.get(model.id);

    const promise = (async () => {
        const ort = await loadRuntime();
        const bytes = await fetchModel(model, onProgress);
        return ort.InferenceSession.create(bytes, {
            executionProviders: ['wasm'],
            graphOptimizationLevel: 'all',
        });
    })();

    // Stored before it resolves so two images picked in quick succession share
    // one download, and dropped again on failure so a retry is not stuck with
    // a rejected promise.
    sessions.set(model.id, promise);
    promise.catch(() => sessions.delete(model.id));
    return promise;
}
